/* Search YouTube channels via Piped API, preview videos, and subscribe via sources.create. */

import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Search,
  Youtube,
  Star,
  Users,
  Video,
  Eye,
  Clock,
  ExternalLink,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import { useYouTubeSearch } from "@/hooks/useYouTubeSearch";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import type {
  YouTubeChannelResult,
  YouTubeVideoPreview,
  TrendingYouTubeChannel,
} from "@/types/youtubeSearch";

const CATEGORY_OPTIONS = [
  { value: "technology", label: "Technology" },
  { value: "gaming", label: "Gaming" },
  { value: "music", label: "Music" },
  { value: "education", label: "Education" },
  { value: "entertainment", label: "Entertainment" },
  { value: "news", label: "News" },
  { value: "sports", label: "Sports" },
  { value: "science", label: "Science" },
  { value: "cooking", label: "Cooking" },
  { value: "fitness", label: "Fitness" },
  { value: "finance", label: "Finance" },
  { value: "comedy", label: "Comedy" },
] as const;

interface YouTubeSearchFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function YouTubeSearchForm({ onSuccess, footerContainer }: YouTubeSearchFormProps) {
  const { results, isSearching, error: searchError, search } = useYouTubeSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<YouTubeChannelResult | null>(null);
  const [videos, setVideos] = useState<YouTubeVideoPreview[]>([]);
  const [isFetchingVideos, setIsFetchingVideos] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [starred, setStarred] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [popularChannels, setPopularChannels] = useState<TrendingYouTubeChannel[]>([]);
  const [isFetchingPopular, setIsFetchingPopular] = useState(false);
  const [popularCategory, setPopularCategory] = useState("technology");
  const [showPopular, setShowPopular] = useState(false);

  const createSource = useMutation(api.sources.create);
  const fetchVideosAction = useAction(api.youtubeSearch.fetchChannelVideos);
  const fetchPopularAction = useAction(api.youtubeSearch.fetchPopularChannels);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);
  const portalTargetRef = useRef<HTMLDivElement>(null);

  const loadVideos = useCallback(async (channelId: string) => {
    setIsFetchingVideos(true);
    setVideosError(null);
    setVideos([]);
    try {
      const data = await fetchVideosAction({ channelId });
      setVideos(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load videos";
      setVideosError(message);
    } finally {
      setIsFetchingVideos(false);
    }
  }, [fetchVideosAction]);

  const loadPopular = useCallback(async (category: string) => {
    setIsFetchingPopular(true);
    try {
      const data = await fetchPopularAction({ category, limit: 20 });
      setPopularChannels(data);
    } catch {
      setPopularChannels([]);
    } finally {
      setIsFetchingPopular(false);
    }
  }, [fetchPopularAction]);

  function handleTogglePopular() {
    if (!showPopular) {
      setShowPopular(true);
      setSelected(null);
      setVideos([]);
      loadPopular(popularCategory);
    } else {
      setShowPopular(false);
    }
  }

  function handleCategoryChange(category: string) {
    setPopularCategory(category);
    loadPopular(category);
  }

  function handleSelectPopularChannel(channel: TrendingYouTubeChannel) {
    setShowPopular(false);
    const asResult: YouTubeChannelResult = {
      id: channel.id,
      name: channel.name,
      description: "",
      thumbnail: channel.thumbnail,
      subscribers: channel.subscribers,
      videoCount: 0,
      verified: false,
      url: channel.url,
    };
    handleSelectChannel(asResult);
  }

  function handleSearch() {
    search(searchQuery);
    setSelected(null);
    setVideos([]);
    setVideosError(null);
    setShowPopular(false);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  }

  function handleSelectChannel(channel: YouTubeChannelResult) {
    setSelected(channel);
    setSubmitError(null);
    setVideos([]);
    setVideosError(null);
    if (channel.id) {
      loadVideos(channel.id);
    }
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  const canSubmit = selected !== null && !isSubmitting;

  async function handleSubmit() {
    if (!selected) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createSource({
        name: selected.name,
        type: "youtube",
        url: selected.url,
        tags: tags.length > 0 ? tags : undefined,
        starred: starred || undefined,
        thumbnail: selected.thumbnail || undefined,
        description: selected.description || undefined,
        subscriberCount: selected.subscribers || undefined,
        videoCount: selected.videoCount || undefined,
      });
      posthog.capture(Events.SOURCE_ADDED, {
        sourceType: "youtube",
        starred,
        channelName: selected.name,
      });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to subscribe";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div ref={portalTargetRef} className="absolute" />

      {/* Search section */}
      <div className="space-y-2">
        <Label>Search YouTube Channels</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by channel name or topic..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
          >
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>
        {searchError && <p className="text-destructive text-sm">{searchError}</p>}
      </div>

      {/* Browse Popular toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleTogglePopular}
        className="w-full"
      >
        <TrendingUp className="size-4" />
        {showPopular ? "Hide Popular Channels" : "Browse Popular Channels"}
      </Button>

      {/* Popular channels section */}
      {showPopular && (
        <PopularChannelsSection
          popularChannels={popularChannels}
          isFetchingPopular={isFetchingPopular}
          popularCategory={popularCategory}
          onCategoryChange={handleCategoryChange}
          onSelectChannel={handleSelectPopularChannel}
        />
      )}

      {/* Search results — horizontal scroll of channel cards */}
      {results.length > 0 && !showPopular && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {results.map((channel) => (
            <button
              key={channel.id}
              type="button"
              className={`flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer hover:bg-muted/50 shrink-0 w-[100px] ${
                selected?.id === channel.id ? "border-primary bg-muted/50" : "border-border"
              }`}
              onClick={() => handleSelectChannel(channel)}
            >
              <ChannelAvatar thumbnail={channel.thumbnail} name={channel.name} size="md" />
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {channel.name}
              </span>
              {channel.verified && (
                <CheckCircle2 className="size-3 text-primary shrink-0" />
              )}
              {channel.subscribers >= 0 && (
                <span className="text-[10px] text-muted-foreground text-center truncate w-full">
                  {formatCount(channel.subscribers)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Selected channel detail card */}
      {selected && (
        <SelectedChannelDetail
          selected={selected}
          videos={videos}
          isFetchingVideos={isFetchingVideos}
          videosError={videosError}
          starred={starred}
          onStarredChange={setStarred}
          tags={tags}
          onTagsChange={setTags}
          availableTags={availableTags}
          onCreateTag={handleCreateTag}
          portalTargetRef={portalTargetRef}
        />
      )}

      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Subscribe
        </Button>,
        footerContainer,
      ) : (
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Subscribe
        </Button>
      )}
    </div>
  );
}

// ─── Popular channels sub-component ───

interface PopularChannelsSectionProps {
  popularChannels: TrendingYouTubeChannel[];
  isFetchingPopular: boolean;
  popularCategory: string;
  onCategoryChange: (category: string) => void;
  onSelectChannel: (channel: TrendingYouTubeChannel) => void;
}

function PopularChannelsSection({
  popularChannels,
  isFetchingPopular,
  popularCategory,
  onCategoryChange,
  onSelectChannel,
}: PopularChannelsSectionProps) {
  return (
    <div className="space-y-3">
      {/* Category filter pills */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {CATEGORY_OPTIONS.map((cat) => (
          <button
            key={cat.value}
            type="button"
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
              popularCategory === cat.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            onClick={() => onCategoryChange(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isFetchingPopular && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Loading channels...</span>
        </div>
      )}

      {/* Channel list */}
      {!isFetchingPopular && popularChannels.length > 0 && (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {popularChannels.map((channel, i) => (
            <button
              key={channel.id}
              type="button"
              className="flex items-center gap-2.5 w-full p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors text-left"
              onClick={() => onSelectChannel(channel)}
            >
              <span className="size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <ChannelAvatar thumbnail={channel.thumbnail} name={channel.name} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium leading-tight truncate">{channel.name}</p>
                <p className="text-[10px] text-muted-foreground truncate">{channel.url.replace("https://www.youtube.com/", "")}</p>
              </div>
              {channel.subscribers >= 0 && (
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {formatCount(channel.subscribers)} subs
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {!isFetchingPopular && popularChannels.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">No channels found for this category.</p>
      )}
    </div>
  );
}

// ─── Selected channel detail sub-component ───

interface SelectedChannelDetailProps {
  selected: YouTubeChannelResult;
  videos: YouTubeVideoPreview[];
  isFetchingVideos: boolean;
  videosError: string | null;
  starred: boolean;
  onStarredChange: (starred: boolean) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  onCreateTag: (tag: string) => Promise<void>;
  portalTargetRef: React.RefObject<HTMLDivElement | null>;
}

function SelectedChannelDetail({
  selected,
  videos,
  isFetchingVideos,
  videosError,
  starred,
  onStarredChange,
  tags,
  onTagsChange,
  availableTags,
  onCreateTag,
  portalTargetRef,
}: SelectedChannelDetailProps) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      {/* Header: avatar + basic info */}
      <div className="flex gap-3">
        <ChannelAvatar thumbnail={selected.thumbnail} name={selected.name} size="lg" />
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold leading-tight">{selected.name}</p>
            {selected.verified && (
              <CheckCircle2 className="size-3.5 text-primary shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            {selected.subscribers >= 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Users className="size-3" />
                {formatCount(selected.subscribers)} subscribers
              </span>
            )}
            {selected.videoCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Video className="size-3" />
                {formatCount(selected.videoCount)} videos
              </span>
            )}
          </div>
          <a
            href={selected.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
          >
            <ExternalLink className="size-3" />
            View on YouTube
          </a>
        </div>
      </div>

      {/* Description */}
      {selected.description && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
          {selected.description}
        </p>
      )}

      {/* Channel details */}
      <div className="text-xs space-y-1 border-t border-border pt-2">
        <DetailRow label="Channel ID" value={selected.id} />
        <DetailRow label="Channel URL" value={selected.url} isUrl />
      </div>

      {/* Recent videos preview */}
      <div className="space-y-2 border-t border-border pt-2">
        <p className="text-xs font-medium text-muted-foreground">
          Recent Videos {videos.length > 0 && `(${videos.length})`}
        </p>
        {isFetchingVideos && (
          <div className="flex items-center gap-2 py-3 justify-center">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Loading videos...</span>
          </div>
        )}
        {videosError && (
          <p className="text-destructive text-xs">{videosError}</p>
        )}
        {videos.length > 0 && (
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {videos.map((video, i) => (
              <VideoRow key={`${video.url}-${i}`} video={video} />
            ))}
          </div>
        )}
      </div>

      {/* Tags & Star */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onStarredChange(!starred)}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <Star className={`size-5 ${starred ? "fill-current text-foreground" : ""}`} />
          </button>
          <span className="text-xs text-muted-foreground">
            {starred ? "Starred — new videos go to queue" : "Star to prioritize"}
          </span>
        </div>

        <div className="space-y-2">
          <Label>Tags</Label>
          <SourceTagSelect
            selectedTags={tags}
            onTagsChange={onTagsChange}
            availableTags={availableTags}
            onCreateTag={onCreateTag}
            portalContainer={portalTargetRef}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Video preview row ───

function VideoRow({ video }: { video: YouTubeVideoPreview }) {
  return (
    <div className="flex gap-2 p-2 rounded-md border border-border">
      {video.thumbnail ? (
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-20 h-12 rounded-sm object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="w-20 h-12 rounded-sm bg-muted flex items-center justify-center shrink-0">
          <Youtube className="size-5 text-muted-foreground/50" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <p className="text-xs font-medium leading-tight line-clamp-1">{video.title}</p>
        <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
          {video.duration > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Clock className="size-2.5" />
              {formatDuration(video.duration)}
            </span>
          )}
          {video.views > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Eye className="size-2.5" />
              {formatCount(video.views)} views
            </span>
          )}
          {video.uploadedDate && (
            <span>{video.uploadedDate}</span>
          )}
          {video.url && (
            <a
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-primary hover:underline"
            >
              <ExternalLink className="size-2.5" />
              watch
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Channel avatar ───

function ChannelAvatar({
  thumbnail,
  name,
  size,
}: {
  thumbnail: string;
  name: string;
  size: "sm" | "md" | "lg";
}) {
  const sizeClasses = {
    sm: "size-10",
    md: "size-12",
    lg: "size-16",
  };
  const iconSizes = {
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
  };

  if (thumbnail) {
    return (
      <img
        src={thumbnail}
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover bg-muted shrink-0`}
      />
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-muted flex items-center justify-center shrink-0`}>
      <Youtube className={`${iconSizes[size]} text-muted-foreground/50`} />
    </div>
  );
}

// ─── Detail row ───

function DetailRow({ label, value, isUrl }: { label: string; value: string; isUrl?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-28">{label}</span>
      {isUrl ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary truncate inline-flex items-center gap-0.5 hover:underline"
        >
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate">{value}</span>
        </a>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </div>
  );
}

// ─── Helpers ───

function formatCount(count: number): string {
  if (count >= 1_000_000_000) return `${(count / 1_000_000_000).toFixed(1)}B`;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
